import { Box, Button, ClickAwayListener, Grow, IconButton, MenuList, Paper, Popper } from "@mui/material";
import React, { useCallback, useMemo, useState, useId } from "react";

const Dropdown = ({
  icon,
  buttonProps = {},
  children,
  buttonText,
  hideOnMobile = false,
  disabled = false,
  placement = "bottom-end",
  menuWidth = 200,
  TransitionComponent = Grow,
  className,
  "data-testid": dataTestId,
}) => {
  const [open, setOpen] = useState(false);
  const anchorRef = React.useRef(null);

  const menuId = `dropdown-menu-${useId()}`;

  // Helper to map placement to transformOrigin values
  const getTransformOrigin = (placementValue) => {
    switch (placementValue) {
      case "bottom-start":
        return "top left";
      case "bottom-end":
        return "top right";
      case "top-start":
        return "bottom left";
      case "top-end":
        return "bottom right";
      default:
        return "top right";
    }
  };

  const handleToggle = useCallback(() => {
    if (!disabled) {
      setOpen((prevOpen) => !prevOpen);
    }
  }, [disabled]);

  const handleClose = useCallback((event) => {
    if (anchorRef.current && anchorRef.current.contains(event?.target)) {
      return;
    }
    setOpen(false);
  }, []);

  const handleListKeyDown = useCallback((event) => {
    if (event.key === "Tab") {
      event.preventDefault();
      setOpen(false);
    } else if (event.key === "Escape") {
      setOpen(false);
    }
  }, []);

  // Wrap children with onClick handlers in a memoized callback
  const childrenWithProps = useMemo(() => {
    if (!children) {
      console.warn("Dropdown: No children provided to dropdown menu");
      return null;
    }

    return React.Children.map(children, (child) => {
      if (!React.isValidElement(child)) {
        console.warn("Dropdown: Invalid child element provided");
        return null;
      }

      return React.cloneElement(child, {
        onClick: (event) => {
          if (child.props.onClick) {
            child.props.onClick(event);
          }
          handleClose(event);
        },
      });
    });
  }, [children, handleClose]);

  const buttonPropsWithAccessibility = {
    ...buttonProps,
    "aria-controls": open ? menuId : undefined,
    "aria-haspopup": true,
    "aria-expanded": open ? true : false,
    disabled,
    ref: anchorRef,
    onClick: handleToggle,
    className: `dropdown-trigger ${buttonProps.className || ""}`,
    "data-testid": `${dataTestId ? `${dataTestId}-trigger` : "dropdown-trigger"}`,
  };

  return (
    <Box className={`dropdown-container ${className || ""}`} sx={{ display: hideOnMobile ? { xs: "none", md: "block" } : "block" }} data-testid={dataTestId}>
      {buttonText ? (
        <Button startIcon={icon} {...buttonPropsWithAccessibility}>
          {buttonText}
        </Button>
      ) : (
        <IconButton {...buttonPropsWithAccessibility}>{icon}</IconButton>
      )}
      <Popper
        open={open}
        anchorEl={anchorRef.current}
        role="menu"
        placement={placement}
        transition
        disablePortal
        sx={{ zIndex: 1300 }}
        data-testid={`${dataTestId ? `${dataTestId}-popper` : "dropdown-popper"}`}
      >
        {({ TransitionProps, placement: popperPlacement }) => (
          <TransitionComponent
            {...TransitionProps}
            style={{
              transformOrigin: getTransformOrigin(popperPlacement),
            }}
          >
            <Paper elevation={3} sx={{ mt: 1, minWidth: menuWidth }} data-testid={`${dataTestId ? `${dataTestId}-paper` : "dropdown-paper"}`}>
              <ClickAwayListener onClickAway={handleClose}>
                <MenuList autoFocusItem={open} id={menuId} onKeyDown={handleListKeyDown} data-testid={`${dataTestId ? `${dataTestId}-menu` : "dropdown-menu"}`}>
                  {childrenWithProps}
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </TransitionComponent>
        )}
      </Popper>
    </Box>
  );
};

export default Dropdown;
